import {inject, injectable} from 'inversify';
import {makeAutoObservable, observable} from 'mobx';

import type {Nullable} from '../../utils/utility-types';

import {logger} from '../../utils/logger';
import {LocalStorageService} from './LocalStorage.service';

@injectable()
export class DraftMessageService {
  private DEBOUNCE_TIMEOUT = 300;
  private debounceTimers = new Map<
    string,
    {abortController: AbortController; timeout: number}
  >();
  private draftMap = observable.map<string, string>();
  private STORAGE_KEY = 'd_msgs';

  constructor(
    @inject(LocalStorageService)
    private readonly localStorageService: LocalStorageService,
  ) {
    makeAutoObservable(this, undefined, {autoBind: true});
    this.init();
  }

  async init() {
    try {
      const dtoJson = await this.localStorageService.getItem(this.STORAGE_KEY);
      const dto = JSON.parse(dtoJson || '{}') as DraftMessageService.DTO;

      this.draftMap.replace(new Map(Object.entries(dto)));
    } catch (e) {
      logger.error(e);
    }
  }

  delete(props: DraftMessageService.GetValueProps) {
    this.processValue({id: props.id, value: null});
  }

  getValue({id}: DraftMessageService.GetValueProps) {
    return this.draftMap.get(id);
  }

  setValue(props: DraftMessageService.SetValueProps) {
    this.processValue(props);
  }

  private processValue({id, value}: DraftMessageService.SetValueProps) {
    const existingDebounce = this.debounceTimers.get(id);

    // Cancel the previous call if it exists
    if (existingDebounce) {
      clearTimeout(existingDebounce.timeout);
      existingDebounce.abortController.abort(); // Abort the previous request
    }

    const abortController = new AbortController();

    const timeout = +setTimeout(() => {
      if (!value) {
        this.draftMap.delete(id);
      } else {
        this.draftMap.set(id, value);
      }

      // Cleanup after execution
      this.debounceTimers.delete(id);
      this.saveToStorage();
    }, this.DEBOUNCE_TIMEOUT);

    // Store the timeout and AbortController for potential cancellation
    this.debounceTimers.set(id, {abortController, timeout});
  }

  private saveToStorage() {
    const dto: DraftMessageService.DTO = Object.fromEntries(
      this.draftMap.entries(),
    ) as DraftMessageService.DTO;

    const json = JSON.stringify(dto);

    try {
      this.localStorageService.setItem(this.STORAGE_KEY, json);
    } catch (e) {
      logger.error(e);
    }
  }
}

export namespace DraftMessageService {
  export type DTO = Record<string, string>;

  export type GetValueProps = {id: string};

  export type SetValueProps = {
    id: string;
    value: Nullable<string>;
  };
}
